import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  getScenarioSettings, updateScenarioSettings, getScenarioImpact,
  getCategories, createCategory, updateCategory, deleteCategory,
  getGroups, createGroup, updateGroup, deleteGroup,
  getSites, createSite, updateSite, deleteSite,
  seedData, seedTranslations,
  getUsers, approveUser, updateUserRole, deleteUser, getAuditLog,
  getExternalCompanies, createExternalCompany, approveExternalCompany,
  getPendingMaterials, approveMaterial, getCategoriesFlat,
  getUnits,
} from '@/lib/api';

const SCENARIO_PRESETS = {
  conservative: { internal_window_days: 60, external_enabled: false, approval_required: true, strict_documentation: true },
  balanced: { internal_window_days: 30, external_enabled: true, approval_required: true, strict_documentation: false },
  aggressive: { internal_window_days: 14, external_enabled: true, approval_required: false, strict_documentation: false },
  custom: {},
};

/**
 * Encapsulates all data-loading + mutation handlers for the Admin dashboard.
 * Keeps AdminPage.jsx focused purely on layout/rendering.
 */
export function useAdminData() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isGroupAdmin = user?.role === 'group_admin';

  // Data state
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [externalCompanies, setExternalCompanies] = useState([]);
  const [pendingMaterialsList, setPendingMaterialsList] = useState([]);
  const [auditData, setAuditData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [impact, setImpact] = useState(null);
  const [units, setUnits] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditFilters, setAuditFilters] = useState({ entity_type: '', action: '', page: 1, limit: 20 });
  const [auditLoading, setAuditLoading] = useState(false);
  const [companyDialog, setCompanyDialog] = useState({ open: false });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, parentId: null, editData: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        getScenarioSettings(), getCategories(), getGroups(), getSites(), getUsers(), getCategoriesFlat(),
      ];
      if (isAdmin) promises.push(getExternalCompanies());
      if (isAdmin) promises.push(getPendingMaterials());

      const results = await Promise.all(promises);
      setSettings(results[0].data);
      setCategories(results[1].data);
      setGroups(results[2].data);
      setSites(results[3].data);
      setUsers(results[4].data);
      setFlatCategories((results[5].data || []).map((c) => ({ ...c, level: 0 })));
      if (isAdmin && results[6]) setExternalCompanies(results[6].data);
      if (isAdmin && results[7]) setPendingMaterialsList(results[7].data);

      try {
        const impactRes = await getScenarioImpact();
        setImpact(impactRes.data);
      } catch { /* impact is optional */ }

      try {
        const unitRes = await getUnits();
        setUnits(unitRes.data || []);
      } catch { /* units are optional */ }
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error(t('common.loadError'));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, t]);

  const loadAuditLog = useCallback(async (filters) => {
    setAuditLoading(true);
    try {
      const res = await getAuditLog(filters);
      setAuditData(res.data);
    } catch { /* ignore */ } finally { setAuditLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadAuditLog(auditFilters); }, [auditFilters, loadAuditLog]);

  // Handlers
  const handleModeChange = (mode) => {
    setSettings((prev) => ({ ...prev, mode, ...(mode !== 'custom' ? SCENARIO_PRESETS[mode] : {}) }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateScenarioSettings(settings);
      toast.success(t('admin.settingsSaved'));
      const impactRes = await getScenarioImpact();
      setImpact(impactRes.data);
    } catch { toast.error(t('admin.settingsError')); }
    finally { setSaving(false); }
  };

  const handleSeedData = async () => {
    try { await seedData(); await seedTranslations(); toast.success(t('admin.seedSuccess')); loadData(); }
    catch { toast.error(t('admin.seedError')); }
  };

  const handleApproveUser = async (userId, approve) => {
    try { await approveUser(userId, approve); toast.success(approve ? t('admin.userApproved') : t('admin.userRevoked')); loadData(); }
    catch { toast.error(t('admin.userApproveFailed')); }
  };

  const handleApproveCompany = async (companyId, status) => {
    try { await approveExternalCompany(companyId, status); toast.success(status === 'approved' ? t('admin.companyApproved') : t('admin.companyRejected')); loadData(); }
    catch { toast.error(t('admin.companyFailed')); }
  };

  const handleApproveMaterial = async (materialId, status) => {
    try {
      await approveMaterial(materialId, status);
      toast.success(status === 'approved' ? 'Material freigegeben' : 'Material abgelehnt');
      loadData();
    } catch {
      toast.error(t('admin.page.materialApproveError'));
    }
  };

  const handleCreateGroup = async (data) => {
    try { await createGroup(data); toast.success(t('admin.groupCreated')); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || t('admin.groupError')); }
  };

  const handleUpdateGroup = async (groupId, data) => {
    try { await updateGroup(groupId, data); toast.success('Gruppe aktualisiert'); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Aktualisieren'); }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Gruppe wirklich löschen?')) return;
    try { await deleteGroup(groupId); toast.success(t('admin.page.groupDeleted')); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || t('admin.common.deleteError')); }
  };

  const handleCreateSite = async (data) => {
    try { await createSite(data); toast.success(t('admin.siteCreated')); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || t('admin.siteError')); }
  };

  const handleUpdateSite = async (siteId, data) => {
    try { await updateSite(siteId, data); toast.success('Site aktualisiert'); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Aktualisieren'); }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Site wirklich löschen?')) return;
    try { await deleteSite(siteId); toast.success(t('admin.page.siteDeleted')); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || t('admin.common.deleteError')); }
  };

  const handleRoleChange = async (userId, role, siteId) => {
    try { await updateUserRole(userId, role, siteId); toast.success('Rolle aktualisiert'); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Rollenänderung'); }
  };

  const handleDeleteUser = async (userId) => {
    try { await deleteUser(userId); toast.success(t('admin.userDeleted')); loadData(); }
    catch (e) { toast.error(e.response?.data?.detail || t('admin.userDeleteFailed')); }
  };

  const handleCreateCompany = async (data) => {
    try { await createExternalCompany(data); toast.success(t('admin.companyCreated') || 'Firma erstellt'); setCompanyDialog({ open: false }); loadData(); }
    catch { toast.error(t('admin.companyError') || 'Fehler'); }
  };

  const handleCreateCategory = async (data) => {
    try {
      if (categoryDialog.editData) {
        await updateCategory(categoryDialog.editData.node_id, data);
      } else {
        await createCategory({ ...data, parent_node_id: categoryDialog.parentId || null });
      }
      toast.success(t('admin.categorySaved'));
      setCategoryDialog({ open: false, parentId: null, editData: null });
      loadData();
    } catch { toast.error(t('admin.categoryError')); }
  };

  const handleDeleteCategory = async (nodeId) => {
    try { await deleteCategory(nodeId); toast.success(t('admin.categoryDeleted')); loadData(); }
    catch { toast.error(t('admin.categoryDeleteError')); }
  };

  return {
    t, isAdmin, isGroupAdmin,
    // data
    settings, setSettings, categories, flatCategories, groups, sites, users,
    externalCompanies, pendingMaterialsList, auditData, impact, units,
    // ui
    loading, saving, auditFilters, setAuditFilters, auditLoading,
    companyDialog, setCompanyDialog, categoryDialog, setCategoryDialog,
    // actions
    loadData,
    handleModeChange, handleSaveSettings, handleSeedData,
    handleApproveUser, handleApproveCompany, handleApproveMaterial,
    handleCreateGroup, handleUpdateGroup, handleDeleteGroup,
    handleCreateSite, handleUpdateSite, handleDeleteSite,
    handleRoleChange, handleDeleteUser, handleCreateCompany,
    handleCreateCategory, handleDeleteCategory,
  };
}
